import { useState, useEffect, useCallback } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  makeStyles,
  Menu,
  MenuItem,
  Box,
  Tooltip,
  Divider,
  ListItemIcon,
  ListItemText,
  Button,
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';

import Brightness4Icon from '@material-ui/icons/Brightness4';
import Brightness7Icon from '@material-ui/icons/Brightness7';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import HelpIcon from '@material-ui/icons/HelpOutline';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import RefreshIcon from '@material-ui/icons/Refresh';
import FlashOnIcon from '@material-ui/icons/FlashOn';
import GettingStartedIcon from '@material-ui/icons/CheckCircle';
import LiveHelpIcon from '@material-ui/icons/LiveHelp';
import GroupIcon from '@material-ui/icons/Group';
import CategoryIcon from '@material-ui/icons/Category';
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import CreateIcon from '@material-ui/icons/Create';
import {
  useApi,
  configApiRef,
  identityApiRef,
  storageApiRef,
  appThemeApiRef,
} from '@backstage/core-plugin-api';
import { useNavigate } from 'react-router-dom';
import { WHATS_NEW_MANUAL_TRIGGER_KEY } from '../../constants/userSettingsKeys';
import { useSidebarPreferences } from '../Root/SidebarPreferencesContext';
import {
  SIDEBAR_ITEMS_CONFIG,
  normalizePath,
  DEFAULT_LANDING_PAGE,
} from '../Root/sidebarConstants';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import { Link } from 'react-router-dom';

import LogoFull from '../Root/LogoFull';
import { BAYER_NAVY } from '../../theme/bayerTheme';
import {
  dismissStagingBanner,
  readStagingBannerDismissed,
  TopNavRebrandNotice,
  TopNavStagingSnackbar,
} from './TopNavBanners';

/** Fixed height of the top navigation toolbar in pixels. */
export const TOP_NAV_HEIGHT = 56;

function isStagingEnvironment(authEnvironment?: string): boolean {
  return authEnvironment === 'np' || authEnvironment === 'local';
}

/** Derive a human-readable label for the user's landing page. */
const formatLandingPageDisplay = (landingPage: string | undefined): string => {
  const rawPath = landingPage || DEFAULT_LANDING_PAGE;
  const normalized = normalizePath(rawPath);
  const configEntry = SIDEBAR_ITEMS_CONFIG[normalized];
  if (configEntry) return configEntry.text;
  if (normalized === DEFAULT_LANDING_PAGE) return 'Search';
  return rawPath
    .slice(1)
    .split('-')
    .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

// Help menu items
const HELP_MENU_ITEMS: {
  path?: string;
  hrefConfigKey?: string;
  label: string;
  icon: React.ComponentType<{ fontSize?: 'small' }>;
}[] = [
  {
    path: '/getting-started',
    label: 'Getting Started',
    icon: GettingStartedIcon,
  },
  {
    path: '/docs/default/component/backstage',
    label: 'IDP Docs',
    icon: MenuBookIcon,
  },
  { path: '/help', label: 'Help', icon: HelpIcon },
  { path: '/qeta', label: 'Q&A', icon: LiveHelpIcon },
  {
    hrefConfigKey: 'app.support.teams.backstageChannelUrl',
    label: 'IDP | Teams',
    icon: GroupIcon,
  },
];

// Quick Actions menu items
const QUICK_ACTIONS_MENU_ITEMS = [
  {
    path: '/catalog',
    label: 'Catalog',
    icon: CategoryIcon,
    description: 'Browse all components',
  },
  {
    path: '/docs',
    label: 'TechDocs',
    icon: LibraryBooksIcon,
    description: 'Browse documentation',
  },
  {
    path: '/catalog-import',
    label: 'Register',
    icon: AddCircleOutlineIcon,
    description: 'Register a component',
  },
  {
    path: '/create',
    label: 'Create',
    icon: CreateIcon,
    description: 'Scaffold from template',
  },
];

const useStyles = makeStyles(theme => ({
  appBar: {
    backgroundColor: (theme.palette as any).navigation?.background || BAYER_NAVY,
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    zIndex: theme.zIndex.drawer + 1,
  },
  toolbar: {
    position: 'relative',
    minHeight: TOP_NAV_HEIGHT,
    height: TOP_NAV_HEIGHT,
    padding: theme.spacing(0, 2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0, 1),
    },
  },
  centerSection: {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(calc(-50% - 100px))',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    minWidth: 0,
    maxWidth: 'calc(100% - 740px)',
    padding: theme.spacing(0, 3, 0, 0.5),
    [theme.breakpoints.down('lg')]: {
      transform: 'translateX(calc(-50% - 84px))',
      maxWidth: 'calc(100% - 680px)',
    },
    [theme.breakpoints.down('md')]: {
      transform: 'translateX(calc(-50% - 68px))',
      maxWidth: 'calc(100% - 560px)',
    },
    '@media (max-width: 1100px)': {
      display: 'none',
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexShrink: 0,
  },
  logoWrapper: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  deploymentBranch: {
    maxWidth: 220,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    padding: theme.spacing(0.35, 1),
    borderRadius: theme.shape.borderRadius,
    border: '1px solid rgba(255,255,255,0.5)',
    backgroundColor: 'rgba(255,255,255,0.12)',
    color: theme.palette.common.white,
    fontSize: '0.72rem',
    fontWeight: 600,
    [theme.breakpoints.down('sm')]: {
      maxWidth: 112,
      fontSize: '0.65rem',
      padding: theme.spacing(0.25, 0.6),
    },
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flexShrink: 0,
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(0.25),
    },
  },
  navButton: {
    color: theme.palette.common.white,
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.78rem',
    padding: '2px 8px',
    minWidth: 'auto',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: 32,
      padding: 4,
      '& .MuiButton-label': {
        fontSize: 0,
      },
      '& .MuiButton-startIcon': {
        margin: 0,
      },
      '& .MuiButton-endIcon': {
        display: 'none',
      },
    },
  },
  navButtonIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 16,
    height: 16,
    borderRadius: '50%',
    border: `1.5px solid ${theme.palette.common.white}`,
  },
  iconButton: {
    color: theme.palette.common.white,
    padding: theme.spacing(0.75),
    '& .MuiSvgIcon-root': {
      fontSize: '1.15rem',
    },
  },
  profileSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    cursor: 'pointer',
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  profileAvatar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 30,
    height: 30,
    '& > *': {
      width: '30px !important',
      height: '30px !important',
    },
  },
  userName: {
    color: theme.palette.common.white,
    fontSize: '0.82rem',
    fontWeight: 500,
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  menuPaper: {
    minWidth: 260,
    borderRadius: 12,
    marginTop: theme.spacing(0.5),
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
  },
  menuDivider: {
    margin: theme.spacing(0.5, 0),
  },
  menuItem: {
    padding: theme.spacing(1, 2),
    fontSize: '0.85rem',
    gap: theme.spacing(1.5),
    '& .MuiListItemIcon-root': {
      minWidth: 'unset',
    },
  },
  themeToggleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.5, 2),
  },
  themeLabel: {
    fontSize: '0.82rem',
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  themeToggleButton: {
    padding: 6,
    borderRadius: 8,
    color: theme.palette.text.primary,
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(255,255,255,0.08)'
        : 'rgba(0,0,0,0.04)',
    '&:hover': {
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(255,255,255,0.15)'
          : 'rgba(0,0,0,0.08)',
    },
  },
}));

export const TopNav = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const storageApi = useApi(storageApiRef);
  const { landingPage } = useSidebarPreferences();
  const [isReady, setIsReady] = useState(false);
  const [userName, setUserName] = useState('');
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  // Header menu anchor states
  const [helpMenuAnchor, setHelpMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [quickActionsMenuAnchor, setQuickActionsMenuAnchor] =
    useState<null | HTMLElement>(null);

  // Theme toggle
  const appThemeApi = useApi(appThemeApiRef);
  const [activeThemeId, setActiveThemeId] = useState<string | undefined>(
    appThemeApi.getActiveThemeId(),
  );

  useEffect(() => {
    const subscription = appThemeApi.activeThemeId$().subscribe(themeId => {
      setActiveThemeId(themeId);
    });
    return () => subscription.unsubscribe();
  }, [appThemeApi]);

  const isDark = activeThemeId === 'dark';

  const handleThemeToggle = () => {
    appThemeApi.setActiveThemeId(isDark ? 'light' : 'dark');
  };

  useEffect(() => {
    identityApi.getProfileInfo().then(info => {
      setUserName(info.displayName || 'User');
      setIsReady(true);
    }).catch(() => {
      // Profile fetch failed (e.g. transient network error); still mark ready so
      // TopNav is not hidden indefinitely. userName remains as its empty default.
      setIsReady(true);
    });
  }, [identityApi]);

  // What's New
  const handleOpenWhatsNew = useCallback(() => {
    const userSettingsApi = storageApi.forBucket('user-settings');
    void userSettingsApi.set(WHATS_NEW_MANUAL_TRIGGER_KEY, true);
  }, [storageApi]);

  // Help menu handlers
  const handleHelpMenuOpen = (event: React.MouseEvent<HTMLElement>) =>
    setHelpMenuAnchor(event.currentTarget);
  const handleHelpMenuClose = () => setHelpMenuAnchor(null);
  const handleHelpClick = (item: (typeof HELP_MENU_ITEMS)[number]) => {
    handleHelpMenuClose();
    if (item.hrefConfigKey) {
      const href = configApi.getOptionalString(item.hrefConfigKey);
      if (href) {
        window.open(href, '_blank', 'noopener,noreferrer');
      }
    } else if (item.path) {
      navigate(item.path);
    }
  };

  // Quick Actions menu handlers
  const handleQuickActionsMenuOpen = (event: React.MouseEvent<HTMLElement>) =>
    setQuickActionsMenuAnchor(event.currentTarget);
  const handleQuickActionsMenuClose = () => setQuickActionsMenuAnchor(null);
  const handleQuickActionsNavigate = (path: string) => {
    handleQuickActionsMenuClose();
    navigate(path);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleSettingsClick = () => {
    navigate('/settings');
    handleMenuClose();
  };

  const handleLogout = async () => {
    handleMenuClose();
    await identityApi.signOut();
    window.location.reload();
  };

  const handleLogoClick = () => {
    const targetPage = landingPage || '/';
    navigate(targetPage);
  };

  const tooltipText = `Your current landing page: ${formatLandingPageDisplay(
    landingPage,
  )}`;

  const authEnvironment = configApi.getOptionalString('auth.environment');
  const isStaging = isStagingEnvironment(authEnvironment);
  const deploymentEnvironment =
    configApi.getOptionalString('app.deployment.environment') ??
    authEnvironment;
  const deploymentBranch =
    deploymentEnvironment === 'np'
      ? configApi.getOptionalString('app.deployment.branch')?.trim()
      : undefined;
  const [isStagingSnackbarOpen, setIsStagingSnackbarOpen] = useState(
    () => isStaging && !readStagingBannerDismissed(),
  );

  useEffect(() => {
    if (isStaging && !readStagingBannerDismissed()) {
      setIsStagingSnackbarOpen(true);
    }
  }, [isStaging]);

  const handleDismissStagingSnackbar = () => {
    dismissStagingBanner();
    setIsStagingSnackbarOpen(false);
  };

  useEffect(() => {
    if (!isReady || !isStaging || !isStagingSnackbarOpen) {
      return undefined;
    }

    const timeout = window.setTimeout(handleDismissStagingSnackbar, 20_000);
    return () => window.clearTimeout(timeout);
  }, [isReady, isStaging, isStagingSnackbarOpen]);

  if (!isReady) {
    return null;
  }

  return (
    <>
      <AppBar position="fixed" className={classes.appBar}>
        {isStaging && isStagingSnackbarOpen ? (
          <TopNavStagingSnackbar
            open
            onClose={handleDismissStagingSnackbar}
          />
        ) : null}
        <Toolbar className={classes.toolbar}>
          <div className={classes.leftSection}>
            <Tooltip title={tooltipText} arrow placement="bottom">
              <Link
                to={landingPage || '/'}
                className={classes.logoWrapper}
                aria-label={`Go to ${formatLandingPageDisplay(landingPage)}`}
                onClick={handleLogoClick}
              >
                <LogoFull />
              </Link>
            </Tooltip>
            {deploymentBranch && (
              <Tooltip title={`NP branch: ${deploymentBranch}`}>
                <Box
                  component="span"
                  className={classes.deploymentBranch}
                  aria-label={`NP branch: ${deploymentBranch}`}
                >
                  NP · {deploymentBranch}
                </Box>
              </Tooltip>
            )}
          </div>

          <div className={classes.centerSection}>
            <TopNavRebrandNotice />
          </div>

          <div className={classes.rightSection}>
            {/* Get Help Menu */}
            <Button
              onClick={handleHelpMenuOpen}
              className={classes.navButton}
              startIcon={<HelpIcon style={{ fontSize: 16 }} />}
              endIcon={
                helpMenuAnchor ? (
                  <ArrowDropUpIcon style={{ fontSize: 16 }} />
                ) : (
                  <ArrowDropDownIcon style={{ fontSize: 16 }} />
                )
              }
            >
              Help
            </Button>
            <Menu
              anchorEl={helpMenuAnchor}
              open={Boolean(helpMenuAnchor)}
              onClose={handleHelpMenuClose}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {HELP_MENU_ITEMS.map(item => {
                const MenuIcon = item.icon;
                return (
                  <MenuItem
                    key={item.label}
                    onClick={() => handleHelpClick(item)}
                  >
                    <ListItemIcon>
                      <MenuIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={item.label} />
                  </MenuItem>
                );
              })}
            </Menu>

            {/* Quick Actions Menu */}
            <Button
              onClick={handleQuickActionsMenuOpen}
              className={classes.navButton}
              startIcon={
                <span className={classes.navButtonIcon}>
                  <FlashOnIcon style={{ fontSize: 10 }} />
                </span>
              }
              endIcon={
                quickActionsMenuAnchor ? (
                  <ArrowDropUpIcon style={{ fontSize: 16 }} />
                ) : (
                  <ArrowDropDownIcon style={{ fontSize: 16 }} />
                )
              }
            >
              Quick Actions
            </Button>
            <Menu
              anchorEl={quickActionsMenuAnchor}
              open={Boolean(quickActionsMenuAnchor)}
              onClose={handleQuickActionsMenuClose}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {QUICK_ACTIONS_MENU_ITEMS.map(item => {
                const MenuIcon = item.icon;
                return (
                  <MenuItem
                    key={item.path}
                    onClick={() => handleQuickActionsNavigate(item.path)}
                  >
                    <ListItemIcon>
                      <MenuIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      secondary={item.description}
                    />
                  </MenuItem>
                );
              })}
            </Menu>

            {/* What's New */}
            <Button
              onClick={handleOpenWhatsNew}
              className={classes.navButton}
              startIcon={<RefreshIcon style={{ fontSize: 16 }} />}
            >
              What's New
            </Button>

            {/* User Profile */}
            <Tooltip title="Edit My Settings" arrow>
              <Box
                className={classes.profileSection}
                role="button"
                tabIndex={0}
                onClick={handleSettingsClick}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleSettingsClick();
                  }
                }}
              >
                <Box className={classes.profileAvatar}>
                  <UserSettingsSignInAvatar />
                </Box>
                <Typography className={classes.userName}>{userName}</Typography>
              </Box>
            </Tooltip>

            {/* More Options (three-dot) */}
            <Tooltip title="More options">
              <IconButton
                className={classes.iconButton}
                onClick={handleMenuClick}
                aria-label="more options"
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>

          {/* Three-dot Menu */}
          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={handleMenuClose}
            getContentAnchorEl={null}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{ className: classes.menuPaper }}
          >
            {/* Theme Toggle */}
            <Box className={classes.themeToggleRow}>
              <Typography className={classes.themeLabel}>
                {isDark ? (
                  <Brightness4Icon style={{ fontSize: '1rem' }} />
                ) : (
                  <Brightness7Icon style={{ fontSize: '1rem' }} />
                )}
                {isDark ? 'Dark Mode' : 'Light Mode'}
              </Typography>
              <Tooltip
                title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                arrow
              >
                <IconButton
                  className={classes.themeToggleButton}
                  onClick={handleThemeToggle}
                  size="small"
                  aria-label={
                    isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'
                  }
                >
                  {isDark ? (
                    <Brightness7Icon style={{ fontSize: '1rem' }} />
                  ) : (
                    <Brightness4Icon style={{ fontSize: '1rem' }} />
                  )}
                </IconButton>
              </Tooltip>
            </Box>

            <Divider className={classes.menuDivider} />

            {/* Logout */}
            <MenuItem
              className={classes.menuItem}
              onClick={handleLogout}
              style={{ color: '#ef5350' }}
            >
              <ListItemIcon>
                <ExitToAppIcon
                  style={{ color: '#ef5350', fontSize: '1.1rem' }}
                />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
    </>
  );
};
